/**
 * @voltxampere/persistence — Settings store (Sprint 23 C1)
 *
 * Single-row IndexedDB store keyed by `SETTINGS_SENTINEL_KEY`.
 * Holds the entire AYARLAR persistence — theme, locale, AC/transient
 * defaults, custom shortcuts, onboarding tour state, a11y prefs.
 *
 * Public API:
 *
 *  - getSettings()              — read the row, returns `null` if not yet written
 *  - mergeSettings(patch)       — partial update (theme + locale + ...)
 *  - clearSettings()            — wipe the row (called from "Tümünü temizle")
 *
 * `mergeSettings` philosophy: atomic read + spread patch + write.
 * Caller passes only the fields they want to change; absent fields
 * stay untouched. `updatedAt` is auto-stamped.
 *
 * Defensive shape validation: IDB returns `unknown`, so each read
 * checks `typeof === "object"` and field-level types before
 * trusting them. A future schema migration that drops/renames a
 * field surfaces as fallback to defaults rather than a runtime
 * crash.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { openDb, requestToPromise, transactionDone } from "./db.js";
import { SETTINGS_SENTINEL_KEY, STORE_SETTINGS } from "./types.js";
/**
 * Read the settings row. Returns `null` if the row has never been
 * written (fresh install, post-clear). Defensive shape check —
 * if the stored object lacks the sentinel key we treat it as
 * missing.
 */
export async function getSettings() {
    const db = await openDb();
    const tx = db.transaction(STORE_SETTINGS, "readonly");
    const store = tx.objectStore(STORE_SETTINGS);
    const raw = await requestToPromise(store.get(SETTINGS_SENTINEL_KEY));
    await transactionDone(tx);
    if (raw === undefined || raw === null)
        return null;
    if (typeof raw !== "object")
        return null;
    const obj = raw;
    if (obj.key !== SETTINGS_SENTINEL_KEY)
        return null;
    // Trust the rest — every consumer falls back when a field is
    // missing. The TypeScript narrowing reflects the contract.
    return obj;
}
/**
 * Merge a partial settings patch into the existing row. Read +
 * spread + write atomically inside a single readwrite transaction.
 * Stamps `updatedAt`.
 */
export async function mergeSettings(patch, now = Date.now()) {
    const db = await openDb();
    const tx = db.transaction(STORE_SETTINGS, "readwrite");
    const store = tx.objectStore(STORE_SETTINGS);
    const existingRaw = await requestToPromise(store.get(SETTINGS_SENTINEL_KEY));
    const existing = existingRaw !== undefined &&
        existingRaw !== null &&
        typeof existingRaw === "object" &&
        existingRaw.key === SETTINGS_SENTINEL_KEY
        ? existingRaw
        : {};
    const next = {
        ...existing,
        ...patch,
        key: SETTINGS_SENTINEL_KEY,
        updatedAt: now,
    };
    await requestToPromise(store.put(next));
    await transactionDone(tx);
    return next;
}
/** Wipe the settings row (AYARLAR "Tümünü temizle" path). */
export async function clearSettings() {
    const db = await openDb();
    const tx = db.transaction(STORE_SETTINGS, "readwrite");
    const store = tx.objectStore(STORE_SETTINGS);
    await requestToPromise(store.delete(SETTINGS_SENTINEL_KEY));
    await transactionDone(tx);
}
//# sourceMappingURL=settings-store.js.map